import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { TypingAnimation } from "@/registry/magicui/typing-animation";
import {
  CalendarCheck,
  Bot,
  User,
  Briefcase,
  Rocket,
  Megaphone,
  CheckCircle2,
  Clock,
  Star,
  MoreVertical,
  Sparkles,
  BellRing,
} from "lucide-react";

const scenarios = [
  {
    id: "freelancer",
    label: "Freelancer",
    icon: Briefcase,
    messages: [
      { role: "user", name: "Sarah", text: "Hey, I need a brand identity designed. Are you available?" },
      { role: "ai", text: "Thanks for reaching out! I'd love to learn more. What's your timeline for this project?" },
      { role: "user", name: "Sarah", text: "Looking to launch in 6 weeks" },
      { role: "ai", text: "Got it. And what's your budget range? This helps me tailor the right package." },
      { role: "user", name: "Sarah", text: "Around $3-5k" },
      { role: "ai", text: "Perfect fit. I'm booking you a 20-min discovery call." },
      { role: "action", text: "Discovery call booked for Tue 2pm", icon: CalendarCheck },
    ],
  },
  {
    id: "founder",
    label: "Founder",
    icon: Rocket,
    messages: [
      { role: "user", name: "Mike", text: "Need help building an MVP app. Can you do full-stack?" },
      { role: "ai", text: "Absolutely. What kind of app are you building, and do you have a spec or wireframes?" },
      { role: "user", name: "Mike", text: "A marketplace for local services. Have rough wireframes" },
      { role: "ai", text: "Nice. What launch date are you aiming for, and what's the budget?" },
      { role: "user", name: "Mike", text: "2 months, around $15k" },
      { role: "ai", text: "This is a strong fit. Let me get you a quick call to scope it out." },
      { role: "action", text: "Strategy call booked for Wed 11am", icon: CalendarCheck },
    ],
  },
  {
    id: "agency",
    label: "Agency",
    icon: Megaphone,
    messages: [
      { role: "user", name: "Priya", text: "We're an e-commerce brand doing $2M/yr. Need a growth agency." },
      { role: "ai", text: "Great to hear from you. What channels are you currently active on?" },
      { role: "user", name: "Priya", text: "Meta ads and email, want to scale into TikTok" },
      { role: "ai", text: "And what's your monthly marketing budget?" },
      { role: "user", name: "Priya", text: "$30k/month" },
      { role: "ai", text: "High-priority lead flagged. Routing to your partnerships team now." },
      { role: "action", text: "Priority lead sent to your team", icon: Star },
    ],
  },
];

/** Must match TypingAnimation `duration` for user/AI bubbles so the next message appears after typing finishes. */
const DEMO_TYPE_MS_PER_CHAR = 30;
const DEMO_PAUSE_AFTER_LINE_MS = 400;
const DEMO_ACTION_HOLD_MS = 700;
const DEMO_FIRST_MESSAGE_MS = 600;

const flowAccents = {
  amber: {
    rowActive: "bg-amber-500/12 border-amber-500/35",
    rowCompleted: "bg-amber-500/[0.08] border-amber-500/25",
    rowIdle: "bg-amber-950/35 border-amber-500/15",
    iconActive: "bg-amber-500 shadow-lg shadow-amber-500/35",
    iconCompleted: "bg-amber-600",
    iconIdle: "bg-amber-500/25",
    sub: "text-amber-200/40",
    subActive: "text-amber-200/65",
  },
  yellow: {
    rowActive: "bg-yellow-500/10 border-yellow-400/35",
    rowCompleted: "bg-yellow-500/[0.08] border-yellow-500/28",
    rowIdle: "bg-yellow-950/30 border-yellow-500/14",
    iconActive: "bg-yellow-500 shadow-lg shadow-yellow-500/30",
    iconCompleted: "bg-yellow-600",
    iconIdle: "bg-yellow-500/25",
    sub: "text-yellow-200/38",
    subActive: "text-yellow-100/60",
  },
  emerald: {
    rowActive: "bg-emerald-500/12 border-emerald-500/35",
    rowCompleted: "bg-emerald-500/[0.08] border-emerald-400/28",
    rowIdle: "bg-emerald-950/35 border-emerald-500/14",
    iconActive: "bg-emerald-500 shadow-lg shadow-emerald-500/30",
    iconCompleted: "bg-emerald-600",
    iconIdle: "bg-emerald-500/25",
    sub: "text-emerald-200/40",
    subActive: "text-emerald-100/60",
  },
  rose: {
    rowActive: "bg-rose-500/12 border-rose-500/35",
    rowCompleted: "bg-rose-500/[0.08] border-rose-500/28",
    rowIdle: "bg-rose-950/40 border-rose-500/16",
    iconActive: "bg-rose-600 shadow-lg shadow-rose-500/35",
    iconCompleted: "bg-rose-600",
    iconIdle: "bg-rose-500/25",
    sub: "text-rose-200/40",
    subActive: "text-rose-100/58",
  },
  violet: {
    rowActive: "bg-violet-500/12 border-violet-500/35",
    rowCompleted: "bg-violet-500/[0.08] border-violet-400/28",
    rowIdle: "bg-violet-950/40 border-violet-500/16",
    iconActive: "bg-violet-600 shadow-lg shadow-violet-500/30",
    iconCompleted: "bg-violet-600",
    iconIdle: "bg-violet-500/25",
    sub: "text-violet-200/40",
    subActive: "text-violet-100/60",
  },
};

function FlowStep({ icon: Icon, label, sublabel, active, completed, accent }) {
  const a = flowAccents[accent];
  const row = active ? a.rowActive : completed ? a.rowCompleted : a.rowIdle;
  const iconBox = active ? a.iconActive : completed ? a.iconCompleted : a.iconIdle;
  const labelCls = active ? "text-white" : completed ? "text-white/85" : "text-white/48";
  const subCls = active ? a.subActive : a.sub;
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-500 ${row}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-500 ${iconBox}`}>
        {completed ? <CheckCircle2 className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4 text-white/90" />}
      </div>
      <div className="min-w-0">
        <p className={`text-xs font-medium transition-colors duration-500 ${labelCls}`}>{label}</p>
        <p className={`text-[10px] transition-colors duration-500 ${subCls}`}>{sublabel}</p>
      </div>
    </div>
  );
}

function FlowConnector({ active, lineClass }) {
  return (
    <div className="flex justify-center">
      <div className={`w-px h-4 transition-colors duration-500 ${active ? lineClass : "bg-white/5"}`} />
    </div>
  );
}

function ChatMessage({ msg, index }) {
  if (msg.role === "action") {
    const Icon = msg.icon || CheckCircle2;
    return (
      <div className="flex justify-center w-full min-w-0 px-1 animate-slide-up-chat" style={{ animationDelay: `${index * 0.1}s` }}>
        <div className="relative flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-w-full px-5 py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 via-violet-500 to-fuchsia-600 text-sm font-medium text-white text-center shadow-[0_8px_32px_-8px_rgba(139,92,246,0.45)] ring-1 ring-white/15 break-words">
          <span className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/10 to-transparent pointer-events-none" aria-hidden />
          <Icon className="w-4 h-4 flex-shrink-0 relative opacity-95" />
          <span className="min-w-0 relative">{msg.text}</span>
          <CheckCircle2 className="w-4 h-4 text-white/90 flex-shrink-0 relative" />
        </div>
      </div>
    );
  }

  const isUser = msg.role === "user";
  return (
    <div
      className={`flex w-full min-w-0 items-end gap-3 animate-slide-up-chat ${isUser ? "justify-end" : "justify-start"}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {!isUser && (
        <div className="relative w-9 h-9 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-600/25 ring-2 ring-[#14121a]">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`flex min-w-0 flex-col gap-1 max-w-[min(100%,19rem)] sm:max-w-[min(100%,23rem)] ${isUser ? "items-end" : "items-start"}`}>
        {isUser && (
          <span className="text-[10px] font-medium text-violet-300/50 tracking-wide uppercase mr-1">{msg.name}</span>
        )}
        <div
          className={`w-full min-w-0 py-3 text-[0.9375rem] leading-[1.55] break-words [overflow-wrap:anywhere] ${
            isUser
              ? "px-4 text-white/95 rounded-[1.15rem] rounded-br-md bg-gradient-to-br from-violet-500/20 to-violet-600/[0.12] border border-violet-400/20 shadow-[0_4px_24px_-6px_rgba(0,0,0,0.5)]"
              : "pl-3.5 pr-4 text-white/[0.92] rounded-[1.15rem] rounded-bl-md bg-[#181522] border border-violet-500/[0.12] border-l-[3px] border-l-violet-500/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
          }`}
        >
          <TypingAnimation
            as="span"
            startOnView={false}
            duration={DEMO_TYPE_MS_PER_CHAR}
            className={isUser ? "text-white/95" : "text-white/[0.88]"}
            cursorStyle="line"
            showCursor
          >
            {msg.text}
          </TypingAnimation>
        </div>
      </div>
      {isUser && (
        <div className="w-9 h-9 rounded-2xl bg-[#1c1828] border border-violet-500/20 flex items-center justify-center flex-shrink-0 shadow-md ring-2 ring-[#14121a]">
          <User className="w-4 h-4 text-violet-300/80" />
        </div>
      )}
    </div>
  );
}

export default function DemoSection() {
  const [activeTab, setActiveTab] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [started, setStarted] = useState(false);
  const [flowStep, setFlowStep] = useState(0);
  const sectionRef = useRef(null);
  const chatScrollRef = useRef(null);
  const chatContentRef = useRef(null);

  useLayoutEffect(() => {
    const scrollEl = chatScrollRef.current;
    const contentEl = chatContentRef.current;
    if (!scrollEl || !contentEl) return;

    const pinBottom = () => {
      scrollEl.scrollTop = scrollEl.scrollHeight;
    };

    pinBottom();

    const ro = new ResizeObserver(() => {
      pinBottom();
    });
    ro.observe(contentEl);

    return () => ro.disconnect();
  }, [visibleMessages.length, activeTab, started]);

  const scenario = scenarios[activeTab];

  // Start animation on scroll into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) setStarted(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [started]);

  // Animate messages for current scenario
  useEffect(() => {
    if (!started) return;

    setVisibleMessages([]);
    setFlowStep(0);

    const msgs = scenario.messages;
    const timeouts = [];
    let scheduleAt = DEMO_FIRST_MESSAGE_MS;

    msgs.forEach((msg, i) => {
      const isLast = i === msgs.length - 1;
      const isAction = msg.role === "action";

      timeouts.push(
        setTimeout(() => {
          setVisibleMessages((prev) => [...prev, msg]);

          if (i <= 1) setFlowStep(1);
          else if (i <= 3) setFlowStep(2);
          else if (i <= 5) setFlowStep(3);
          else if (isAction) setFlowStep(4);
        }, scheduleAt)
      );

      if (isLast && isAction) {
        const afterAction = scheduleAt + DEMO_ACTION_HOLD_MS;
        timeouts.push(setTimeout(() => setFlowStep(5), afterAction + 380));
        timeouts.push(setTimeout(() => setFlowStep(6), afterAction + 1100));
      }

      if (msg.role === "action") {
        scheduleAt += DEMO_ACTION_HOLD_MS;
      } else {
        const graphemes = Array.from(msg.text);
        scheduleAt += graphemes.length * DEMO_TYPE_MS_PER_CHAR + DEMO_PAUSE_AFTER_LINE_MS;
      }
    });

    return () => timeouts.forEach(clearTimeout);
  }, [activeTab, started, scenario.messages]);

  const handleTabChange = (idx) => {
    setActiveTab(idx);
    setStarted(true);
  };

  return (
    <section
      id="demo"
      ref={sectionRef}
      data-testid="demo-section"
      className="relative py-14 sm:py-[4.25rem] overflow-hidden"
    >
      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(100vw,640px)] h-[480px] bg-violet-600/[0.035] rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="reveal text-center mb-9">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-violet-300/55 mb-3">
            See it in action
          </p>
          <h2
            data-testid="demo-headline"
            className="text-3xl sm:text-4xl tracking-tight font-semibold mb-2"
          >
            Watch it work.{" "}
            <span className="bg-gradient-to-r from-violet-300 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              In real time.
            </span>
          </h2>
          <p className="text-sm text-violet-200/35 max-w-md mx-auto">
            Pick a persona and watch a full conversation play out.
          </p>
        </div>

        {/* Scenario tabs */}
        <div
          data-testid="demo-tabs"
          className="flex flex-wrap justify-center gap-2 mb-8"
          role="tablist"
          aria-label="Demo scenario"
        >
          {scenarios.map((s, i) => (
            <button
              type="button"
              key={s.id}
              role="tab"
              id={`demo-tab-${s.id}`}
              aria-selected={activeTab === i}
              aria-controls="demo-chat-panel"
              data-testid={`demo-tab-${s.id}`}
              onClick={() => handleTabChange(i)}
              className={`flex items-center gap-2 min-h-11 px-4 py-2.5 rounded-full text-xs font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                activeTab === i
                  ? "bg-violet-500/15 text-violet-100 border border-violet-400/30"
                  : "bg-violet-500/[0.06] text-white/45 border border-transparent hover:bg-violet-500/10 hover:text-white/70"
              }`}
            >
              <s.icon className="w-3.5 h-3.5" />
              {s.label}
            </button>
          ))}
        </div>

        {/* Main demo area: Chat + Flow sidebar */}
        <div className="reveal max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-5 min-w-0">
          {/* Chat window */}
          <div
            id="demo-chat-panel"
            data-testid="demo-chat-window"
            role="tabpanel"
            aria-labelledby={`demo-tab-${scenario.id}`}
            className="min-w-0 rounded-[1.25rem] border border-violet-500/[0.14] bg-[#0c0a10] shadow-[0_24px_80px_-24px_rgba(0,0,0,0.85),0_0_0_1px_rgba(139,92,246,0.06)_inset] overflow-hidden ring-1 ring-white/[0.04]"
          >
            {/* Window chrome */}
            <div className="flex items-center gap-3 px-4 py-2 border-b border-white/[0.05] bg-[#08060c]">
              <div className="flex gap-1.5" aria-hidden>
                <span className="h-2.5 w-2.5 rounded-full bg-white/[0.12]" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/[0.08]" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/[0.08]" />
              </div>
              <span className="text-[10px] text-white/25 font-medium tracking-wide truncate flex-1 text-center pr-14">
                reachmenow.app/demo
              </span>
            </div>

            {/* Chat header */}
            <div className="relative flex items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-violet-500/[0.09] bg-gradient-to-r from-[#100e16] via-[#0e0c12] to-[#100e16]">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-500/25 to-transparent pointer-events-none" aria-hidden />
              <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/90 to-fuchsia-600/90 shadow-lg shadow-violet-600/20 ring-2 ring-[#0c0a10]">
                <Sparkles className="w-[18px] h-[18px] text-white" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-white/90 tracking-tight truncate">
                    ReachMeNow AI
                  </span>
                  <span className="shrink-0 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300/90 ring-1 ring-emerald-400/20">
                    Live
                  </span>
                </div>
                <p className="text-[11px] text-white/35 mt-0.5 truncate">
                  Qualifies leads and books calls for you
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="hidden sm:flex items-center gap-1 rounded-lg bg-white/[0.04] px-2 py-1.5 border border-white/[0.06]">
                  <Clock className="w-3 h-3 text-violet-300/50" aria-hidden />
                  <span className="text-[10px] font-medium text-violet-200/45 whitespace-nowrap">
                    ~2s reply
                  </span>
                </div>
                <button
                  type="button"
                  className="rounded-lg p-2 text-white/25 hover:text-white/45 hover:bg-white/[0.05] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                  aria-label="Chat options"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Chat body */}
            <div
              ref={chatScrollRef}
              className="relative min-h-[340px] max-h-[400px] min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain px-3 py-5 sm:px-5 [scrollbar-width:thin] [scrollbar-color:rgba(139,92,246,0.25)_transparent]"
            >
              <div ref={chatContentRef} className="relative space-y-5">
                {visibleMessages.length === 0 && (
                  <p className="text-center text-xs text-white/20 py-12">Conversation starts shortly.</p>
                )}
                {visibleMessages.map((msg, i) => (
                  <ChatMessage key={`${activeTab}-${i}-${msg.role}-${msg.text?.slice(0, 12)}`} msg={msg} index={i} />
                ))}
              </div>
            </div>

            {/* Composer (decorative) */}
            <div className="border-t border-violet-500/[0.08] bg-[#0a0810] px-3 py-3 sm:px-4">
              <div className="flex items-end gap-2 rounded-xl border border-violet-500/10 bg-[#12101c]/90 px-3 py-2.5 ring-1 ring-black/30">
                <div className="flex-1 min-h-[2.25rem] flex items-center">
                  <span className="text-sm text-white/25 select-none">Message as visitor…</span>
                </div>
                <span className="text-[10px] text-white/15 uppercase tracking-wider shrink-0 pb-0.5 hidden sm:block">
                  Demo
                </span>
              </div>
            </div>
          </div>

          {/* Flow sidebar: behind-the-scenes steps */}
          <div
            data-testid="demo-flow-sidebar"
            className="bg-[#12101c] border border-violet-500/12 rounded-2xl p-4 hidden lg:block"
          >
            <p className="text-[10px] tracking-[0.15em] uppercase font-semibold mb-4 bg-gradient-to-r from-amber-200/85 via-yellow-200/75 to-emerald-200/80 bg-clip-text text-transparent">
              What&apos;s happening
            </p>
            <div className="space-y-2.5">
              <FlowStep
                icon={User}
                label="Inbound received"
                sublabel="New message detected"
                active={flowStep === 1}
                completed={flowStep > 1}
                accent="amber"
              />
              <FlowConnector active={flowStep >= 2} lineClass="bg-amber-400/45" />
              <FlowStep
                icon={Bot}
                label="AI qualifying"
                sublabel="Asking budget & timeline"
                active={flowStep === 2}
                completed={flowStep > 2}
                accent="yellow"
              />
              <FlowConnector active={flowStep >= 3} lineClass="bg-yellow-400/45" />
              <FlowStep
                icon={CheckCircle2}
                label="Lead scored"
                sublabel="Fit confirmed"
                active={flowStep === 3}
                completed={flowStep > 3}
                accent="emerald"
              />
              <FlowConnector active={flowStep >= 4} lineClass="bg-emerald-400/45" />
              <FlowStep
                icon={CalendarCheck}
                label="Action taken"
                sublabel="Call booked or routed"
                active={flowStep === 4}
                completed={flowStep > 4}
                accent="rose"
              />
              <FlowConnector active={flowStep >= 5} lineClass="bg-rose-400/40" />
              <FlowStep
                icon={BellRing}
                label="You're notified"
                sublabel="Push & inbox: meeting request + AI summary"
                active={flowStep === 5}
                completed={flowStep > 5}
                accent="violet"
              />
            </div>

            {flowStep >= 6 && (
              <div className="mt-4 p-3 rounded-xl bg-emerald-500/[0.12] border border-emerald-400/28 animate-fade-in">
                <p className="text-[10px] text-emerald-200 font-semibold flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                  Completed in under 60 seconds
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom stat bar */}
        <div className="reveal max-w-5xl mx-auto mt-6 grid grid-cols-3 gap-3 sm:gap-4">
          {[
            { value: "< 3s", label: "First reply" },
            { value: "100%", label: "Response rate" },
            { value: "0", label: "Missed leads" },
          ].map((stat) => (
            <div
              key={stat.label}
              data-testid={`demo-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-center py-3.5 rounded-xl bg-violet-500/[0.04] border border-violet-500/10"
            >
              <p className="text-lg font-bold bg-gradient-to-r from-violet-300 to-fuchsia-400 bg-clip-text text-transparent">
                {stat.value}
              </p>
              <p className="text-[10px] text-white/30 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
